/**
 * CDN 容灾 IndexedDB 读写工具
 *
 * 用于跨 session 持久化最近一次成功的 CDN 域名，供 Service Worker 在 activate
 * 阶段读取，解决 SW 启动早于主线程 postMessage 导致的时序竞争问题。
 *
 * 同时在主线程（cdn-failure-reporter.tsx）写入，与 localStorage 双写保持一致。
 * localStorage 仍保留，供由 inject-cdn-onerror.mjs 生成的 onerror 内联脚本同步读取。
 */

import { CDN_IDB_NAME, CDN_IDB_STORE, CDN_IDB_KEY, CDN_DOH_IDB_KEY, CDN_FAILOVER_CONFIRMED_IDB_KEY } from '@/const/cdn-failover.mjs';

export interface CdnOkRecord {
  domain: string;
  ts: number;
}

export interface CdnDohRecord {
  ciphertext: string;
  ts: number;
}

/** 打开 DB，若不存在则创建 store */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(CDN_IDB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(CDN_IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** 写入可用 CDN 记录 */
export async function cdnIdbSet(record: CdnOkRecord): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(CDN_IDB_STORE, 'readwrite');
      tx.objectStore(CDN_IDB_STORE).put(record, CDN_IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IDB 不可用时静默忽略，localStorage 仍作为备份
  }
}

/** 读取可用 CDN 记录，失败或不存在返回 null */
export async function cdnIdbGet(): Promise<CdnOkRecord | null> {
  try {
    const db = await openDb();
    return await new Promise<CdnOkRecord | null>((resolve, reject) => {
      const tx = db.transaction(CDN_IDB_STORE, 'readonly');
      const req = tx.objectStore(CDN_IDB_STORE).get(CDN_IDB_KEY);
      req.onsuccess = () => resolve((req.result as CdnOkRecord) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

/** 写入 DoH 查询结果缓存 */
export async function dohIdbSet(record: CdnDohRecord): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(CDN_IDB_STORE, 'readwrite');
      tx.objectStore(CDN_IDB_STORE).put(record, CDN_DOH_IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IDB 不可用时静默忽略
  }
}

/** 读取 DoH 查询结果缓存，失败或不存在返回 null */
export async function dohIdbGet(): Promise<CdnDohRecord | null> {
  try {
    const db = await openDb();
    return await new Promise<CdnDohRecord | null>((resolve, reject) => {
      const tx = db.transaction(CDN_IDB_STORE, 'readonly');
      const req = tx.objectStore(CDN_IDB_STORE).get(CDN_DOH_IDB_KEY);
      req.onsuccess = () => resolve((req.result as CdnDohRecord) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

/** 写入容灾确认标志（tab 关闭后需由主线程清除） */
export async function failoverConfirmedIdbSet(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(CDN_IDB_STORE, 'readwrite');
      tx.objectStore(CDN_IDB_STORE).put(true, CDN_FAILOVER_CONFIRMED_IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {}
}

/** 读取容灾确认标志，不存在或失败返回 false */
export async function failoverConfirmedIdbGet(): Promise<boolean> {
  try {
    const db = await openDb();
    return await new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(CDN_IDB_STORE, 'readonly');
      const req = tx.objectStore(CDN_IDB_STORE).get(CDN_FAILOVER_CONFIRMED_IDB_KEY);
      req.onsuccess = () => resolve(req.result === true);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return false;
  }
}

/** 清除容灾确认标志（tab 关闭时调用，模拟 sessionStorage 的 tab 生命周期） */
export async function failoverConfirmedIdbClear(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(CDN_IDB_STORE, 'readwrite');
      tx.objectStore(CDN_IDB_STORE).delete(CDN_FAILOVER_CONFIRMED_IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {}
}
